import React, { useEffect, useState } from "react";
import { api, ApiError, type Property } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function monthLabel(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

const CATEGORIES = ["repairs", "salary", "electricity", "water", "internet", "food", "cleaning", "tax", "other"] as const;
type Category = (typeof CATEGORIES)[number];
const CATEGORY_KEY: Record<Category, string> = {
  repairs: "catRepairs",
  salary: "catSalary",
  electricity: "catElectricity",
  water: "catWater",
  internet: "catInternet",
  food: "catFood",
  cleaning: "catCleaning",
  tax: "catTax",
  other: "catOther",
};

interface PnlMonth {
  month: string;
  rent: number;
  electricity: number;
  lateFees: number;
  other: number;
  income: number;
  expenses: number;
  net: number;
}
interface Pnl {
  months: PnlMonth[];
  totals: { rent: number; electricity: number; lateFees: number; other: number; income: number; expenses: number; net: number };
}
interface Expense {
  id: string;
  category: Category;
  amount: number;
  spentOn: string;
  note: string;
  propertyLabel: string | null;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

// Profit & loss for the last 6 months plus the expense log that feeds it.
export function ExpensesSection() {
  const { t } = useLanguage();
  const toast = useToast();
  const [pnl, setPnl] = useState<Pnl | null>(null);
  const [expenses, setExpenses] = useState<Expense[] | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [adding, setAdding] = useState(false);
  const [category, setCategory] = useState<Category>("repairs");
  const [amount, setAmount] = useState("");
  const [spentOn, setSpentOn] = useState(today());
  const [note, setNote] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function load() {
    api.get("/api/reports/profit-loss").then(setPnl).catch(() => {});
    api.get("/api/expenses").then((d) => setExpenses(d.expenses)).catch(() => setExpenses([]));
  }
  useEffect(() => {
    load();
    api.get("/api/properties/mine").then((d) => setProperties(d.properties)).catch(() => {});
  }, []);

  async function save() {
    const value = Number(amount);
    if (!Number.isInteger(value) || value < 1 || !spentOn) return setError(t("ownerReports.expInvalid"));
    setBusy(true);
    setError("");
    try {
      await api.post("/api/expenses", {
        category,
        amount: value,
        spentOn,
        note: note.trim() || undefined,
        propertyId: propertyId || null,
      });
      toast(t("ownerReports.expSaved"));
      setAmount("");
      setNote("");
      setAdding(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.del(`/api/expenses/${id}`);
      toast(t("ownerReports.expDeleted"));
      load();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    }
  }

  const line: React.CSSProperties = { display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 };

  return (
    <div style={{ marginTop: 26 }}>
      <div className="section-title" style={{ margin: "0 0 4px", fontSize: 12 }}>
        {t("ownerReports.plHeading")}
      </div>
      <div className="sub" style={{ marginBottom: 10, fontSize: 13 }}>{t("ownerReports.plSub")}</div>

      {pnl && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 18, marginBottom: 10, flexWrap: "wrap" }}>
            <div>
              <div className="reg-eyebrow" style={{ marginBottom: 4 }}>{t("ownerReports.plIncome")}</div>
              <b style={{ fontSize: 18 }}>{fmtINR(pnl.totals.income)}</b>
            </div>
            <div>
              <div className="reg-eyebrow" style={{ marginBottom: 4 }}>{t("ownerReports.plExpenses")}</div>
              <b style={{ fontSize: 18 }}>{fmtINR(pnl.totals.expenses)}</b>
            </div>
            <div>
              <div className="reg-eyebrow" style={{ marginBottom: 4 }}>{t("ownerReports.plNet")}</div>
              <b style={{ fontSize: 18, color: pnl.totals.net < 0 ? "var(--danger)" : "var(--success)" }}>{fmtINR(pnl.totals.net)}</b>
            </div>
          </div>
          <div style={{ borderTop: "1px dashed var(--line)", paddingTop: 6 }}>
            <div style={line}><span className="sub">{t("ownerReports.plRent")}</span><span>{fmtINR(pnl.totals.rent)}</span></div>
            {pnl.totals.electricity > 0 && <div style={line}><span className="sub">{t("ownerReports.plElectricity")}</span><span>{fmtINR(pnl.totals.electricity)}</span></div>}
            {pnl.totals.lateFees > 0 && <div style={line}><span className="sub">{t("ownerReports.plLateFees")}</span><span>{fmtINR(pnl.totals.lateFees)}</span></div>}
            {pnl.totals.other > 0 && <div style={line}><span className="sub">{t("ownerReports.plOther")}</span><span>{fmtINR(pnl.totals.other)}</span></div>}
          </div>
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "var(--ink-faint)", textAlign: "right" }}>
                  <th style={{ textAlign: "left", fontWeight: 700, padding: "4px 0" }} />
                  <th style={{ fontWeight: 700 }}>{t("ownerReports.plIncome")}</th>
                  <th style={{ fontWeight: 700 }}>{t("ownerReports.plExpenses")}</th>
                  <th style={{ fontWeight: 700 }}>{t("ownerReports.plNet")}</th>
                </tr>
              </thead>
              <tbody>
                {pnl.months.map((m) => (
                  <tr key={m.month} style={{ textAlign: "right", borderTop: "1px solid var(--line)" }}>
                    <td style={{ textAlign: "left", padding: "5px 0" }}>{monthLabel(m.month)}</td>
                    <td>{fmtINR(m.income)}</td>
                    <td>{fmtINR(m.expenses)}</td>
                    <td style={{ color: m.net < 0 ? "var(--danger)" : undefined, fontWeight: 700 }}>{fmtINR(m.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card-row" style={{ marginBottom: 8 }}>
        <div className="section-title" style={{ margin: 0, fontSize: 12 }}>{t("ownerReports.expHeading")}</div>
        {!adding && (
          <button type="button" className="link-btn" onClick={() => setAdding(true)}>
            + {t("ownerReports.expAdd")}
          </button>
        )}
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 12 }}>
          {error && <div className="error-banner">{error}</div>}
          <div className="field">
            <label>{t("ownerReports.expCategory")}</label>
            <div className="input-wrap">
              <select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`ownerReports.${CATEGORY_KEY[c]}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label>{t("ownerReports.expAmount")}</label>
            <div className="input-wrap">
              <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="0" />
            </div>
          </div>
          <div className="field">
            <label>{t("ownerReports.expDate")}</label>
            <div className="input-wrap">
              <input type="date" value={spentOn} max={today()} onChange={(e) => setSpentOn(e.target.value)} />
            </div>
          </div>
          {properties.length > 0 && (
            <div className="field">
              <label>{t("ownerReports.expProperty")}</label>
              <div className="input-wrap">
                <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
                  <option value="">{t("ownerReports.expAllProperties")}</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="field">
            <label>{t("ownerReports.expNote")}</label>
            <div className="input-wrap">
              <input value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={save}>
              {t("ownerReports.expSave")}
            </button>
            <button className="btn-outline btn-sm" onClick={() => setAdding(false)}>
              ✕
            </button>
          </div>
        </div>
      )}

      {expenses && expenses.length === 0 && <div className="sub">{t("ownerReports.expEmpty")}</div>}
      {expenses &&
        expenses.slice(0, 20).map((e) => (
          <div className="card-row" key={e.id} style={{ padding: "9px 0", borderBottom: "1px solid var(--line)" }}>
            <div style={{ minWidth: 0 }}>
              <b style={{ fontSize: 13 }}>{t(`ownerReports.${CATEGORY_KEY[e.category]}`)}</b>
              <div className="sub" style={{ fontSize: 12 }}>
                {new Date(e.spentOn).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                {e.propertyLabel ? ` · ${e.propertyLabel}` : ""}
                {e.note ? ` · ${e.note}` : ""}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <b>{fmtINR(e.amount)}</b>
              <div>
                <button type="button" className="link-btn" style={{ fontSize: 11 }} onClick={() => remove(e.id)}>
                  {t("ownerReports.expDelete")}
                </button>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}
